import { styled } from '@mui/material/styles';
import { Box, Container, Typography } from '@mui/material';

export const ProjectsContainer = styled(Container)(({ theme }) => ({
  padding: theme.spacing(8, 0),
  position: 'relative',
  overflow: 'hidden',
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: theme.palette.mode === 'dark' 
      ? 'radial-gradient(circle at 50% 50%, rgba(30, 30, 30, 0.8) 0%, rgba(10, 10, 10, 0.9) 100%)'
      : 'radial-gradient(circle at 50% 50%, rgba(240, 240, 240, 0.8) 0%, rgba(255, 255, 255, 0.9) 100%)',
    zIndex: -1,
  }
}));

export const ProjectsHeader = styled(Box)(({ theme }) => ({
  textAlign: 'center',
  marginBottom: theme.spacing(8),
  position: 'relative',
  '&::after': {
    content: '""',
    position: 'absolute',
    bottom: -theme.spacing(2),
    left: '50%',
    transform: 'translateX(-50%)',
    width: '100px',
    height: '4px',
    background: `linear-gradient(90deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
    borderRadius: '2px',
  }
}));

export const ProjectsGrid = styled(Box)(({ theme }) => ({
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fill, minmax(350px, 1fr))',
  gap: theme.spacing(4),
  marginTop: theme.spacing(4),
  [theme.breakpoints.down('sm')]: {
    gridTemplateColumns: '1fr',
  }
}));

export const ProjectCard = styled(Box)(({ theme }) => ({
  position: 'relative',
  borderRadius: theme.shape.borderRadius * 2,
  overflow: 'hidden',
  backgroundColor: theme.palette.background.paper,
  boxShadow: theme.palette.mode === 'dark'
    ? '0 10px 30px rgba(0, 0, 0, 0.5)'
    : '0 10px 30px rgba(0, 0, 0, 0.1)',
  transition: 'box-shadow 0.4s ease, background-color 0.3s ease',
  cursor: 'pointer',
  display: 'flex',
  flexDirection: 'column',
  height: '100%',
  '&:hover': {
    boxShadow: theme.palette.mode === 'dark'
      ? '0 15px 40px rgba(0, 0, 0, 0.6)'
      : '0 15px 40px rgba(0, 0, 0, 0.15)',
    '& .project-image': {
      transform: 'scale(1.05)',
    },
    '& .project-overlay': {
      opacity: 1,
      visibility: 'visible'
    }
  }
}));

export const ProjectImage = styled('img')(({ theme }) => ({
  width: '100%',
  height: '250px',
  objectFit: 'contain',
  objectPosition: 'center',
  transition: 'transform 0.6s ease',
  display: 'block',
  backgroundColor: theme.palette.background.paper,
  padding: theme.spacing(3),
  borderBottom: `1px solid ${theme.palette.mode === 'dark' 
    ? 'rgba(255, 255, 255, 0.1)' 
    : 'rgba(0, 0, 0, 0.1)'}`
}));

export const ProjectOverlay = styled(Box)(({ theme }) => ({
  position: 'absolute',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  background: theme.palette.mode === 'dark'
    ? 'linear-gradient(to bottom, rgba(0, 0, 0, 0.95), rgba(0, 0, 0, 0.98))'
    : 'linear-gradient(to bottom, rgba(0, 0, 0, 0.85), rgba(0, 0, 0, 0.95))',
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  alignItems: 'center',
  padding: theme.spacing(3),
  opacity: 0,
  visibility: 'hidden',
  transition: 'opacity 0.4s ease, visibility 0.4s ease',
  zIndex: 2,
  backdropFilter: 'blur(3px)',
  WebkitBackdropFilter: 'blur(3px)',
}));

export const ProjectTitle = styled(Typography)(({ theme }) => ({
  color: theme.palette.common.white,
  fontWeight: 700,
  marginBottom: theme.spacing(1),
  textAlign: 'center',
  fontSize: '1.5rem',
  textShadow: '0 2px 4px rgba(0,0,0,0.5)',
}));

export const ProjectDescription = styled(Typography)(({ theme }) => ({
  color: theme.palette.common.white,
  textAlign: 'center',
  marginBottom: theme.spacing(2),
  fontSize: '0.9rem',
  opacity: 0.9,
  textShadow: '0 1px 2px rgba(0,0,0,0.5)',
  maxWidth: '90%',
  margin: '0 auto',
}));

export const ProjectTechStack = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexWrap: 'wrap',
  justifyContent: 'center',
  gap: theme.spacing(1),
  marginTop: theme.spacing(2),
}));

export const TechChip = styled(Box)(({ theme }) => ({
  padding: theme.spacing(0.5, 1),
  borderRadius: theme.shape.borderRadius,
  backgroundColor: theme.palette.primary.main,
  color: theme.palette.common.white,
  fontSize: '0.75rem',
  fontWeight: 500,
  boxShadow: '0 2px 4px rgba(0, 0, 0, 0.2)',
}));

export const ProjectLinks = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'center',
  gap: theme.spacing(2),
  marginTop: theme.spacing(2),
}));

export const ProjectLink = styled('a')(({ theme }) => ({
  color: theme.palette.common.white,
  backgroundColor: 'rgba(255, 255, 255, 0.2)',
  borderRadius: '50%',
  width: '40px',
  height: '40px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  transition: 'all 0.3s ease',
  '&:hover': {
    backgroundColor: theme.palette.primary.main,
    transform: 'translateY(-3px)',
  }
}));

export const ProjectInfo = styled(Box)(({ theme }) => ({
  padding: theme.spacing(3),
  backgroundColor: theme.palette.background.paper,
  flexGrow: 1,
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'space-between',
  borderTop: `1px solid ${theme.palette.mode === 'dark' 
    ? 'rgba(255, 255, 255, 0.1)' 
    : 'rgba(0, 0, 0, 0.1)'}`,
}));

export const ProjectCategory = styled(Typography)(({ theme }) => ({
  color: theme.palette.primary.main,
  fontWeight: 600,
  fontSize: '0.85rem',
  marginBottom: theme.spacing(1),
  textTransform: 'uppercase',
  letterSpacing: '0.05em',
}));

export const ProjectName = styled(Typography)(({ theme }) => ({
  fontWeight: 700,
  marginBottom: theme.spacing(1),
  fontSize: '1.25rem',
}));

export const ProjectSummary = styled(Typography)(({ theme }) => ({
  color: theme.palette.text.secondary,
  marginBottom: theme.spacing(2),
  fontSize: '0.9rem',
}));

export const ProjectTags = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexWrap: 'wrap',
  gap: theme.spacing(1),
}));

export const ProjectTag = styled(Box)(({ theme }) => ({
  padding: theme.spacing(0.25, 1),
  borderRadius: theme.shape.borderRadius,
  backgroundColor: theme.palette.mode === 'dark' 
    ? 'rgba(255, 255, 255, 0.1)' 
    : 'rgba(0, 0, 0, 0.05)',
  color: theme.palette.text.secondary,
  fontSize: '0.75rem',
}));

export const ProjectHero = styled(Box)(({ theme }) => ({
  position: 'relative',
  height: '40vh',
  minHeight: '300px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  overflow: 'hidden',
  marginBottom: theme.spacing(8),
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: theme.palette.mode === 'dark'
      ? 'linear-gradient(135deg, rgba(0, 0, 0, 0.8) 0%, rgba(0, 0, 0, 0.6) 100%)'
      : 'linear-gradient(135deg, rgba(255, 255, 255, 0.8) 0%, rgba(255, 255, 255, 0.6) 100%)',
    zIndex: 1,
  }
}));

export const ProjectHeroContent = styled(Box)(({ theme }) => ({
  position: 'relative',
  zIndex: 2,
  textAlign: 'center',
  maxWidth: '800px',
  padding: theme.spacing(0, 3),
}));

export const ProjectHeroTitle = styled(Typography)(({ theme }) => ({
  fontWeight: 800,
  fontSize: { xs: '2.5rem', md: '3.75rem' },
  marginBottom: theme.spacing(2),
  lineHeight: 1.2,
  textShadow: theme.palette.mode === 'dark' 
    ? '3px 3px 6px rgba(0,0,0,0.5)'
    : '2px 2px 4px rgba(0,0,0,0.3)',
  letterSpacing: '-0.02em',
}));

export const ProjectHeroSubtitle = styled(Typography)(({ theme }) => ({
  fontSize: '1.25rem',
  marginBottom: theme.spacing(3),
  opacity: 0.9,
  textShadow: theme.palette.mode === 'dark'
    ? '1px 1px 3px rgba(0,0,0,0.3)'
    : '1px 1px 2px rgba(0,0,0,0.2)',
  fontWeight: 500,
}));

export const ProjectFilterContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'center',
  flexWrap: 'wrap',
  gap: theme.spacing(2),
  marginBottom: theme.spacing(6),
}));

export const ProjectFilterButton = styled(Box)(({ theme, active }) => ({
  padding: theme.spacing(1, 3),
  borderRadius: theme.shape.borderRadius * 2,
  backgroundColor: active 
    ? theme.palette.primary.main 
    : theme.palette.mode === 'dark'
      ? 'rgba(255, 255, 255, 0.1)'
      : 'rgba(0, 0, 0, 0.05)',
  color: active 
    ? theme.palette.common.white 
    : theme.palette.text.primary,
  fontWeight: 600,
  cursor: 'pointer',
  transition: 'all 0.3s ease, background-color 0.3s ease, color 0.3s ease',
  '&:hover': {
    backgroundColor: active 
      ? theme.palette.primary.dark 
      : theme.palette.mode === 'dark'
        ? 'rgba(255, 255, 255, 0.2)'
        : 'rgba(0, 0, 0, 0.1)',
  }
}));

export const ProjectAnimation = styled(Box)(({ theme }) => ({
  position: 'absolute',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  zIndex: 0,
  overflow: 'hidden',
})); 